
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface MLPredictions {
  salesForecast: {
    nextWeek: number;
    nextMonth: number;
    confidence: number;
  };
  customerSegments: Array<{
    segment: string;
    size: number;
    value: number;
    characteristics: string[];
  }>;
  productRecommendations: Array<{
    productId: string;
    productName: string;
    predictedSales: number;
    reason: string;
  }>;
  anomalies: Array<{
    type: 'revenue' | 'orders' | 'traffic';
    detected: boolean;
    severity: 'low' | 'medium' | 'high';
    description: string;
  }>;
}

export const useMLAnalytics = () => {
  const [mlData, setMLData] = useState<MLPredictions | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const { data: rawData, isLoading } = useQuery({
    queryKey: ['ml-analytics-data'],
    queryFn: async () => {
      // Only fetch essential data for ML analysis - dramatically reduced egress
      const [ordersResult, productsCount, usersCount, recentOrdersResult] = await Promise.all([
        // Get basic revenue data for last 30 days only
        supabase.from('orders')
          .select('total_amount, created_at')
          .eq('payment_status', 'paid')
          .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString())
          .order('created_at', { ascending: false })
          .limit(50), // Reduced from 1000 to 50
        // Use count queries instead of full data
        supabase.from('products').select('*', { count: 'exact', head: true }),
        supabase.from('profiles').select('*', { count: 'exact', head: true }),
        // Get minimal product info for recommendations
        supabase.from('products')
          .select('id, name')
          .eq('status', 'active')
          .limit(10)
      ]);

      return {
        orders: ordersResult.data || [],
        products: recentOrdersResult.data || [],
        users: [], // Empty array since we only need count
        productCount: productsCount.count || 0,
        userCount: usersCount.count || 0
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes cache
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Simulate ML processing
  useEffect(() => {
    if (!rawData || isLoading) return;

    setIsProcessing(true);
    
    // Simulate ML analysis with realistic data
    setTimeout(() => {
      const recentOrders = rawData.orders.slice(0, 30);
      const totalRevenue = recentOrders.reduce((sum, order) => sum + Number(order.total_amount), 0);
      const avgDailyRevenue = totalRevenue / 30;

      const predictions: MLPredictions = {
        salesForecast: {
          nextWeek: avgDailyRevenue * 7 * (1 + Math.random() * 0.2 - 0.1),
          nextMonth: avgDailyRevenue * 30 * (1 + Math.random() * 0.3 - 0.15),
          confidence: 0.75 + Math.random() * 0.2
        },
        customerSegments: [
          {
            segment: 'High Value Customers',
            size: Math.floor(rawData.userCount * 0.2),
            value: totalRevenue * 0.6,
            characteristics: ['High spending', 'Frequent purchases', 'Premium products']
          },
          {
            segment: 'Growing Customers',
            size: Math.floor(rawData.userCount * 0.3),
            value: totalRevenue * 0.25,
            characteristics: ['Increasing purchase frequency', 'Mid-range products', 'Price sensitive']
          },
          {
            segment: 'New Customers',
            size: Math.floor(rawData.userCount * 0.5),
            value: totalRevenue * 0.15,
            characteristics: ['First-time buyers', 'Entry-level products', 'Needs nurturing']
          }
        ],
        productRecommendations: rawData.products.slice(0, 5).map(product => ({
          productId: product.id,
          productName: product.name,
          predictedSales: Math.floor(Math.random() * 50) + 10,
          reason: 'Trending in similar markets'
        })),
        anomalies: [
          {
            type: 'revenue',
            detected: Math.random() > 0.7,
            severity: 'low',
            description: 'Slight dip in weekend sales detected'
          },
          {
            type: 'orders',
            detected: Math.random() > 0.8,
            severity: 'medium',
            description: 'Unusual spike in mobile orders'
          }
        ]
      };

      setMLData(predictions);
      setIsProcessing(false);
    }, 2000);
  }, [rawData, isLoading]);

  return {
    mlData,
    isLoading: isLoading || isProcessing,
    refetch: () => window.location.reload()
  };
};
